<template>
  <div class="emp-notice">
    <!-- 헤더 -->
    <div class="emp-header">
      <button class="back-btn" @click="goBack">
        <v-icon size="24" color="white">mdi-arrow-left</v-icon>
      </button>
      <div class="header-title">공지사항</div>
      <div class="header-spacer"></div>
    </div>

    <!-- 컨텐츠 -->
    <div class="emp-content">
      <!-- 로딩 -->
      <div v-if="loading" class="loading-container">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <!-- 목록 -->
      <div v-else-if="notices.length > 0" class="notice-list">
        <div 
          v-for="notice in notices" 
          :key="notice.nc_idx" 
          class="notice-item"
          @click="goToDetail(notice)"
        >
          <span :class="['mini-badge', `badge-${notice.nc_category.toLowerCase()}`]">
            {{ getCategoryText(notice.nc_category) }}
          </span>
          <span class="item-title">
            {{ notice.nc_title }}
            <v-icon v-if="notice.file_count > 0" size="12" color="grey" class="ml-1">mdi-attachment</v-icon>
            <span v-if="isNew(notice.nc_wdate)" class="new-badge">N</span>
          </span>
          <span class="item-author">{{ notice.nc_type === 'ADMIN' ? '관리자' : notice.nc_sisul_name || '-' }}</span>
          <span class="item-date">{{ formatListDate(notice.nc_wdate) }}</span>
        </div>
      </div>

      <!-- 데이터 없음 -->
      <div v-else class="empty-state">
        <v-icon size="48" color="#ccc">mdi-bulletin-board</v-icon>
        <p>공지사항이 없습니다</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

interface Notice {
  nc_idx: number;
  nc_type: 'ADMIN' | 'CENTER';
  nc_category: string;
  nc_sisul_name: string | null;
  nc_writer_name: string;
  nc_title: string;
  nc_wdate: string;
  file_count: number;
}

const router = useRouter();
const loading = ref(false);
const notices = ref<Notice[]>([]);

const loadNotices = async () => {
  loading.value = true;
  try {
    const response = await axios.get('/api/notice/list', {
      params: { page: 1, limit: 50 },
      withCredentials: true
    });
    
    if (response.data.success) {
      notices.value = response.data.notices;
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.push('/emp/home');
};

const goToDetail = (notice: Notice) => {
  router.push(`/emp/notice/${notice.nc_idx}`);
};

const formatDate = (date: string) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const formatListDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
};

const isNew = (date: string) => {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  return (now.getTime() - d.getTime()) < 3 * 24 * 60 * 60 * 1000;
};

const getCategoryText = (category: string) => {
  switch (category) {
    case 'NOTICE': return '공지';
    case 'UPDATE': return '업데이트';
    case 'EVENT': return '이벤트';
    case 'URGENT': return '긴급';
    default: return category;
  }
};

onMounted(() => {
  loadNotices();
});
</script>

<style scoped lang="scss">
.emp-notice {
  min-height: 100vh;
  background: #f5f5f5;
}

.emp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  
  .back-btn {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
  }
  
  .header-title {
    font-size: 18px;
    font-weight: 600;
  }
  
  .header-spacer {
    width: 32px;
  }
}

.emp-content {
  padding: 16px;
  padding-bottom: 80px;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.notice-list {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.notice-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  
  &:active {
    background: #fafafa;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  .mini-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    margin-right: 8px;
    flex-shrink: 0;
    
    &.badge-notice { background: #e3f2fd; color: #1976d2; }
    &.badge-update { background: #e0f7fa; color: #0097a7; }
    &.badge-event { background: #e8f5e9; color: #388e3c; }
    &.badge-urgent { background: #ffebee; color: #d32f2f; }
  }
  
  .item-title {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    
    .new-badge {
      display: inline-block;
      background: #d32f2f;
      color: white;
      font-size: 9px;
      font-weight: 600;
      padding: 1px 4px;
      border-radius: 3px;
      margin-left: 4px;
      flex-shrink: 0;
    }
  }
  
  .item-author {
    font-size: 10px;
    color: #666;
    margin-left: 8px;
    white-space: nowrap;
  }
  
  .item-date {
    font-size: 10px;
    color: #999;
    margin-left: 6px;
    white-space: nowrap;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  
  p {
    margin-top: 12px;
    font-size: 14px;
  }
}
</style>

